import type { AuthenticityTrigger, AuthenticityVerdict } from "@repo/types/authenticity-check";
import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { PoisonedDataParameters, PoisonedDataTargetTable } from "@/lib/poisonedData/filters";

export interface PoisonedDataMethod {
  slug: string;
  name: string;
  description: string | null;
  target_table: PoisonedDataTargetTable;
  category: "known_poison" | "data_quality" | "ai_assisted" | "advanced";
  risk_level: "low" | "medium" | "high";
  input_schema: Record<string, unknown>;
  default_parameters: PoisonedDataParameters;
  is_enabled: boolean;
}

export interface PoisonedDataBatch {
  id: string;
  method_slug: string;
  target_table: PoisonedDataTargetTable;
  parameters: PoisonedDataParameters;
  match_count: number;
  sample_count: number;
  status: "previewed" | "quarantined" | "deleted" | "failed";
  created_at: string;
}

export interface PoisonedDataPreviewRow {
  targetTable: PoisonedDataTargetTable;
  targetId: string;
  confidence: number;
  reason: string;
  fields: {
    address?: string | null;
    suburb?: string | null;
    postcode?: string | null;
    state?: string | null;
    adapter?: string | string[] | null;
    price?: number | string | null;
    date?: string | null;
    sourceUrl?: string | null;
    jobId?: string | null;
    jobRunId?: string | null;
    queueItemId?: string | null;
  };
  record: Record<string, unknown>;
}

export interface PoisonedDataPreviewResponse {
  success: boolean;
  batch?: PoisonedDataBatch;
  rows?: PoisonedDataPreviewRow[];
  count?: number;
  limit?: number;
  offset?: number;
  error?: string;
}

export interface PoisonedDataAiSetting {
  key: string;
  value: Record<string, unknown>;
  description: string | null;
  updated_at: string;
}

export interface OpenRouterModelOption {
  id: string;
  name?: string;
  contextLength?: number | null;
}

async function invokePoisonedData<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("poisoned-data-cleanup", { body });
  if (error) {
    throw error;
  }
  if (data?.success === false) {
    throw new Error(data.error ?? "Poisoned data cleanup request failed");
  }
  return data as T;
}

export async function fetchPoisonedDataMethods(): Promise<PoisonedDataMethod[]> {
  const data = await invokePoisonedData<{ success: boolean; methods: PoisonedDataMethod[] }>({
    action: "list-methods",
  });
  return data.methods ?? [];
}

export interface PoisonedDataSavedSearch {
  id: string;
  name: string;
  description: string | null;
  target_table: PoisonedDataTargetTable;
  method_slug: string;
  parameters: PoisonedDataParameters;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  last_match_count: number | null;
  last_executed_at: string | null;
}

export async function fetchPoisonedDataSavedSearches(): Promise<PoisonedDataSavedSearch[]> {
  const data = await invokePoisonedData<{
    success: boolean;
    savedSearches: PoisonedDataSavedSearch[];
  }>({ action: "list-saved-searches" });
  return data.savedSearches ?? [];
}

export async function saveCurrentPoisonedDataSearch(input: {
  name: string;
  description?: string;
  targetTable: PoisonedDataTargetTable;
  methodSlug?: string;
  parameters: PoisonedDataParameters;
}): Promise<PoisonedDataSavedSearch> {
  const data = await invokePoisonedData<{ success: boolean; savedSearch: PoisonedDataSavedSearch }>({
    action: "save-search",
    name: input.name,
    description: input.description,
    targetTable: input.targetTable,
    methodSlug: input.methodSlug ?? "custom_filter_builder",
    parameters: input.parameters,
  });
  return data.savedSearch;
}

export async function deletePoisonedDataSavedSearch(id: string): Promise<void> {
  await invokePoisonedData({ action: "delete-saved-search", id });
}

export async function previewPoisonedData(input: {
  methodSlug: string;
  targetTable?: PoisonedDataTargetTable;
  parameters: PoisonedDataParameters;
  limit?: number;
  offset?: number;
}): Promise<PoisonedDataPreviewResponse> {
  return invokePoisonedData<PoisonedDataPreviewResponse>({
    action: "preview",
    methodSlug: input.methodSlug,
    targetTable: input.targetTable ?? input.parameters.targetTable,
    parameters: input.parameters,
    limit: input.limit ?? 50,
    offset: input.offset ?? 0,
  });
}

export async function quarantinePoisonedData(input: {
  batchId: string;
  targetTable: PoisonedDataTargetTable;
  targetIds: string[];
}): Promise<{ success: boolean; quarantined: number }> {
  return invokePoisonedData({
    action: "quarantine",
    ...input,
  });
}

export async function deleteSelectedPoisonedData(input: {
  batchId?: string;
  methodSlug?: string;
  targetTable: PoisonedDataTargetTable;
  targetIds: string[];
}): Promise<{ success: boolean; deleted: number }> {
  return invokePoisonedData({
    action: "delete-selected",
    ...input,
  });
}

export async function deleteCurrentPoisonedDataQuery(input: {
  batchId: string;
}): Promise<{ success: boolean; deleted: number }> {
  return invokePoisonedData({
    action: "delete-current-query",
    batchId: input.batchId,
  });
}

export async function fetchPoisonedDataSettings(): Promise<PoisonedDataAiSetting[]> {
  const data = await invokePoisonedData<{ success: boolean; settings: PoisonedDataAiSetting[] }>({
    action: "get-settings",
  });
  return data.settings ?? [];
}

export async function updatePoisonedDataSettings(
  settings: Array<Pick<PoisonedDataAiSetting, "key" | "value" | "description">>,
): Promise<PoisonedDataAiSetting[]> {
  const data = await invokePoisonedData<{ success: boolean; settings: PoisonedDataAiSetting[] }>({
    action: "update-settings",
    settings,
  });
  return data.settings ?? [];
}

export async function fetchOpenRouterModels(): Promise<{
  models: OpenRouterModelOption[];
  warning?: string;
}> {
  const data = await invokePoisonedData<{
    success: boolean;
    models: Array<OpenRouterModelOption | string>;
    warning?: string;
  }>({
    action: "list-models",
  });
  return {
    models: (data.models ?? []).map((model) =>
      typeof model === "string" ? { id: model, name: model } : model,
    ),
    warning: data.warning,
  };
}

export async function suggestPoisonedDataFilter(input: {
  parameters: PoisonedDataParameters;
  operatorGoal?: string;
  previewRows?: PoisonedDataPreviewRow[];
  model?: string;
  refine?: boolean;
}): Promise<{
  success: boolean;
  suggestion: Record<string, unknown>;
  model: string;
}> {
  return invokePoisonedData({
    action: input.refine ? "ai-refine-filter" : "ai-suggest-filter",
    parameters: input.parameters,
    operatorGoal: input.operatorGoal,
    previewRows: input.previewRows ?? [],
    model: input.model,
  });
}

// ---------------------------------------------------------------------------
// LLM authenticity check ("Jev"): snapshot-authenticity-check edge function

export interface SnapshotAuthenticityCheck {
  id: string;
  property_snapshot_id: string;
  checked_at: string;
  model: string;
  /** P(genuine), 0..1; null when the check failed. */
  score: number | null;
  verdict: AuthenticityVerdict | null;
  reasons: string[];
  triggers: AuthenticityTrigger[];
  /** Tokens and OpenRouter cost (USD); `attempts` is set when a parse failure was retried. */
  usage: { cost?: number | null; totalTokens?: number | null; attempts?: number } | null;
  error: string | null;
}

export interface RecentSnapshotAuthenticityCheck extends SnapshotAuthenticityCheck {
  property_snapshots: { property_id: string; adapter_slug: string | null } | null;
}

export interface AuthenticityCheckOutcome {
  snapshotId: string;
  model: string;
  score: number | null;
  verdict: AuthenticityVerdict | null;
  error: string | null;
  cost: number | null;
}

export interface AuthenticityBatchSummary {
  candidates: number;
  selected: number;
  overCap: number;
  deferred: number;
  checked: number;
  errors: number;
  verdicts: Record<AuthenticityVerdict, number>;
  cost: number;
}

const CHECK_COLUMNS =
  "id, property_snapshot_id, checked_at, model, score, verdict, reasons, triggers, usage, error";

/**
 * snapshot_authenticity_checks (migration 20261005090000) is newer than the generated
 * Database types, and there is no offline type generator, so this one table is read
 * through an untyped client; rows are cast to the interfaces above.
 */
function authenticityChecksTable() {
  return (supabase as unknown as SupabaseClient).from("snapshot_authenticity_checks");
}

async function invokeAuthenticityCheck<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("snapshot-authenticity-check", { body });
  if (error) {
    // Non-2xx replies (403 viewer, 404 snapshot) carry the reason in the JSON body.
    const payload = await (error as { context?: Response }).context?.json?.().catch(() => null);
    throw new Error(payload?.error ?? error.message);
  }
  if (data?.success === false) {
    throw new Error(data.error ?? "Authenticity check request failed");
  }
  return data as T;
}

/** Checks one snapshot now (trigger `manual`); always writes a row, even on failure. */
export async function checkSnapshotAuthenticity(snapshotId: string): Promise<{
  model: string;
  outcomes: AuthenticityCheckOutcome[];
}> {
  return invokeAuthenticityCheck({ action: "check-snapshot", snapshotId });
}

/** Runs the selection policy over recent snapshots (default last 60 min) or one job run. */
export async function runAuthenticityBatch(
  opts: { jobRunId?: string; sinceMinutes?: number } = {},
): Promise<{
  model: string;
  summary: AuthenticityBatchSummary;
  outcomes: AuthenticityCheckOutcome[];
}> {
  return invokeAuthenticityCheck({ action: "run-batch", ...opts });
}

/** Latest check per snapshot, keyed by snapshot id. */
export async function fetchAuthenticityChecks(
  snapshotIds: string[],
): Promise<Record<string, SnapshotAuthenticityCheck>> {
  if (snapshotIds.length === 0) return {};
  const { data, error } = await authenticityChecksTable()
    .select(CHECK_COLUMNS)
    .in("property_snapshot_id", snapshotIds)
    .order("checked_at", { ascending: false })
    .limit(1000);
  if (error) throw error;
  const latest: Record<string, SnapshotAuthenticityCheck> = {};
  for (const row of (data ?? []) as SnapshotAuthenticityCheck[]) {
    latest[row.property_snapshot_id] ??= { ...row, score: toScore(row.score) };
  }
  return latest;
}

/** Most recent checks across all snapshots, for reviewing a check run. */
export async function fetchRecentAuthenticityChecks(
  limit = 50,
): Promise<RecentSnapshotAuthenticityCheck[]> {
  const { data, error } = await authenticityChecksTable()
    .select(`${CHECK_COLUMNS}, property_snapshots(property_id, adapter_slug)`)
    .order("checked_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  // The untyped client types the embed as an array; PostgREST returns one object (many-to-one).
  return ((data ?? []) as unknown as RecentSnapshotAuthenticityCheck[]).map((row) => ({
    ...row,
    score: toScore(row.score),
  }));
}

/** Postgres numeric can arrive as a string. */
function toScore(value: unknown): number | null {
  const n = typeof value === "string" ? Number(value) : value;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}
